import { useState } from 'react';

import { Button, Input, Modal, Typography } from '@/components';
import { useAuthUser, useBrandCreate, useBrandsSearchExternal } from '@/hooks';
import { useTranslation } from 'react-i18next';

import { TeamBrand } from './TeamBrand';
import styles from './TeamBrands.module.css';

interface TeamBrandsProps {
  setBrands: React.Dispatch<React.SetStateAction<Api.Brand[]>>;
  brands: Api.Brand[];
}

export function TeamBrands({ brands, setBrands }: TeamBrandsProps) {
  const [selectedBrand, setSelectedBrand] = useState({} as TypeAny);
  const [createNewBrand, setCreateNewBrand] = useState(false);
  const [formData, setFormData] = useState({} as TypeAny);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const user = useAuthUser();
  const { t } = useTranslation();

  const { isLoading: isCreatingBrand, mutate: brandCreate } = useBrandCreate({
    brand: selectedBrand,
    onSuccess: (data: TypeAny) => {
      if (!data.status) {
        setBrands((prevState) => [...prevState, { ...data }]);
      }
      setCreateNewBrand(false);
    }
  });

  const {
    mutate: brandsSearchExternal,
    isLoading: isSearching,
    brandsExternal
  } = useBrandsSearchExternal(formData.term);

  if (!user) return null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState: TypeAny) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = (e: TypeAny) => {
    e.preventDefault();
    setSelectedBrand({ name: formData.name });
    brandCreate();
  };

  const handleSearch = (event: TypeAny) => {
    event.preventDefault();
    setSelectedBrand({});
    setShowSearchResults(true);
    // Only search if we have a term
    if (formData.term && formData.term.trim()) {
      brandsSearchExternal();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch(event);
    }
  };

  const handleCreateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const handleBrandSelect = (event: TypeAny, brand: Api.Brand) => {
    event.preventDefault();
    event.stopPropagation();
    // Map Brandfetch logo field to logo_url for API
    const brandWithLogoUrl = {
      ...brand,
      logo_url: brand.logo
    };
    setSelectedBrand(brandWithLogoUrl);
    setShowSearchResults(false);
    brandCreate();
  };

  const handleBrandSelectTouch = (event: React.TouchEvent, brand: Api.Brand) => {
    event.preventDefault();
    event.stopPropagation();
    // Use the same logic as handleBrandSelect but for touch events
    handleBrandSelect(event as TypeAny, brand);
  };

  const handleRemove = (id: string) => {
    setBrands((prevState) => prevState.filter((brand) => brand.brand_id !== id));
  };

  return (
    <>
      <div className={styles.teamBrandsHeading}>
        <Typography sizes={['h4']}>{t('teamBrands.heading')}</Typography>

        {user.permissions?.can_add_brands && (
          <div>
            <Button onClick={() => setCreateNewBrand(!createNewBrand)} theme="cta">
              {t('teamBrands.create')}
            </Button>
          </div>
        )}
      </div>

      {!createNewBrand && !brands.filter((activeBrand) => !activeBrand.is_archived).length && (
        <Typography sizes={['h4']}>{t('teamBrands.notFound')}</Typography>
      )}

      {createNewBrand && (
        <Modal handleClose={() => setCreateNewBrand(false)}>
          <div className={styles.teamBrandsInputsModal}>
            <form className={styles.teamBrandsInputs} onSubmit={handleSubmit}>
              <Input
                placeholder={t('teamBrands.name')}
                disabled={isCreatingBrand}
                onChange={handleChange}
                onKeyDown={handleCreateKeyDown}
                name="name"
              />

              <Button
                isLoading={isCreatingBrand}
                disabled={!formData.name}
                type="submit"
                theme="cta"
              >
                {t('teamBrands.createCta')}
              </Button>
            </form>

            <div className={styles.teamBrandsAlternative}>
              <Typography sizes={['label-secondary']}>
                {t('teamBrands.createAlternative')}
              </Typography>
            </div>

            <form className={styles.teamBrandsInputs} onSubmit={handleSearch}>
              <div className={styles.teamBrandsInputsSearch}>
                <Input
                  placeholder={t('teamBrands.term')}
                  disabled={isCreatingBrand}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  name="term"
                />

                {!isSearching &&
                  !isCreatingBrand &&
                  showSearchResults &&
                  brandsExternal?.length > 0 && (
                    <div className={styles.teamBrandsInputsSearchResults}>
                      {brandsExternal?.map((brand: TypeAny) => (
                        <button
                          onClick={(event: TypeAny) => handleBrandSelect(event, brand)}
                          onTouchEnd={(event: React.TouchEvent) =>
                            handleBrandSelectTouch(event, brand)
                          }
                          className={styles.teamBrandsInputsSearchResultsButton}
                          key={`brand-${brand.name}-${brand.domain}`}
                          type="button"
                        >
                          <TeamBrand brand={brand} isSearch />
                        </button>
                      ))}
                    </div>
                  )}
              </div>

              <Button
                disabled={!formData.term || isCreatingBrand}
                isLoading={isSearching}
                type="submit"
                theme="cta"
              >
                {t('teamBrands.searchCta')}
              </Button>
            </form>
          </div>
        </Modal>
      )}

      {brands
        .filter((activeBrand) => !activeBrand.is_archived)
        .map((brand) => (
          <TeamBrand
            key={`brand-${brand.brand_id}`}
            handleRemove={handleRemove}
            setBrands={setBrands}
            brand={brand}
          />
        ))}
    </>
  );
}
